import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ChatService} from '../service/chat.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {ChatViewComponent} from '../chat-view/chat-view.component';
import {MessageViewComponent} from '../message-view/message-view.component';
import {NgClass} from '@angular/common';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faChatbicAi} from '../../util/icons/custom-icons';
import {
  faPlus,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {Project} from '../../../api';
import {ProposalReviewService} from '../../../services/chat/proposal-review.service';

@Component({
  selector: 'app-chat-fab',
  templateUrl: './chat-fab.component.html',
  styleUrls: ['./chat-fab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ChatViewComponent,
    MessageViewComponent,
    NgClass,
    FaIconComponent,
    BicButtonComponent,
  ],
})
export class ChatFabComponent implements OnInit, OnDestroy {
  protected chatService = inject(ChatService);
  protected proposalReview = inject(ProposalReviewService);

  protected readonly faChatbicAi = faChatbicAi;
  protected readonly faPlus = faPlus;
  protected readonly faXmark = faXmark;
  private stateConfigService = inject(StateConfigService);
  private router = inject(Router);
  private projectChangeSub?: Subscription;
  private routerSub?: Subscription;
  private resizeStartX = 0;
  private panelWasResized = false;
  private resizeWidthMoveHandler = (event: MouseEvent) => {
    this.panelWasResized ||= Math.abs(event.clientX - this.resizeStartX) > 3;
    this.chatService.setPanelWidth(window.innerWidth - event.clientX);
  };
  private resizeUpHandler = () => {
    document.removeEventListener('mousemove', this.resizeWidthMoveHandler);
    document.removeEventListener('mouseup', this.resizeUpHandler);
  };

  private resizeListener = () => {
    this.chatService.setPanelWidth(this.chatService.panelWidth);
  };

  ngOnInit(): void {
    this.chatService.isFloatingMode = true;

    const newProjectId = this.stateConfigService.getProjectId();

    // If the project changed since last init, do a full reset
    if (this.chatService.currentProject && this.chatService.currentProject !== newProjectId && newProjectId) {
      this.chatService.floatingChatOpen = false;
      this.chatService.handleProjectChange(newProjectId);
    } else {
      this.chatService.currentProject = newProjectId;
      this.chatService.initializeChat();
    }

    window.addEventListener('beforeunload', this.unloadHandler);

    // Listen for projectChange events (may not fire reliably for all project switches)
    this.projectChangeSub = this.stateConfigService.projectChange.subscribe((project: Project) => {
      if (project?.id && project.id !== this.chatService.currentProject) {
        this.chatService.floatingChatOpen = false;
        this.chatService.handleProjectChange(project.id);
      }
    });

    // Also detect project changes via route navigation (covers cases where projectChange doesn't fire)
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const currentProjectId = this.stateConfigService.getProjectId();
      if (currentProjectId && currentProjectId !== this.chatService.currentProject) {
        this.chatService.floatingChatOpen = false;
        this.chatService.handleProjectChange(currentProjectId);
      }
    });
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy(): void {
    this.chatService.isFloatingMode = false;
    this.projectChangeSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    window.removeEventListener('beforeunload', this.unloadHandler);
    this.resizeUpHandler();
    window.removeEventListener('resize', this.resizeListener);
  }

  togglePanel(): void {
    this.chatService.toggleFloatingChat();
  }

  /**
   * Öffnet den geschlossenen Chat (Button am rechten Bildschirmrand). Auf einer myCheck-Editor-Seite
   * wird der Chat mit Type edit geöffnet.
   */
  openPanel(): void {
    const editorContext = this.currentMycheckEditorContext();
    if (editorContext) {
      void this.chatService.openMycheckEditChat(editorContext.rulesetId, editorContext.mycheckType);
      return;
    }
    this.togglePanel();
  }

  /**
   * Liest Regelset-ID und Typ aus der aktuell aktiven Route, falls diese ein KI-, Hybrid- oder
   * IDS-myCheck-Editor ist (Pfad-Muster `:rulesetId/ki`, `:rulesetId/hybrid` bzw. `:rulesetId/ids`,
   * identisch für den projekt- und den admin-globalen Bereich, siehe `routes.config.ts`). `new` ist der
   * Routen-Platzhalter einer noch nicht gespeicherten Regel und hat keinen Edit-Modus.
   * `adminGlobal`/`?readOnly=true` spiegeln nur die aus der Route ablesbaren Sperren des Editors – eine
   * zur Laufzeit (z. B. über ein bereitgestelltes, nicht per Query-Parameter erkennbares Regelwerk)
   * gesetzte `readOnly`-Sperre kennt diese Route-Prüfung nicht, da chatBIC dafür den geladenen
   * Regelsatz kennen müsste.
   */
  private currentMycheckEditorContext(): { rulesetId: string; mycheckType: 'KI' | 'HYBRID' | 'IDS' } | null {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    const editorTypes: Record<string, 'KI' | 'HYBRID' | 'IDS'> = {
      ':rulesetId/ki': 'KI',
      ':rulesetId/hybrid': 'HYBRID',
      // Regel-Editor eines hybriden myChecks: Edit-Chat für das Set, die geöffnete Regel liest der
      // Server aus der Route (ChatRouteContext).
      ':rulesetId/hybrid/:ruleId': 'HYBRID',
      ':rulesetId/ids': 'IDS',
    };
    const mycheckType = editorTypes[snapshot.routeConfig?.path ?? ''] ?? null;
    if (!mycheckType || snapshot.data['adminGlobal'] === true || snapshot.queryParamMap.get('readOnly') === 'true') {
      return null;
    }
    const rulesetId = snapshot.paramMap.get('rulesetId');
    return rulesetId && rulesetId !== 'new' ? {rulesetId, mycheckType} : null;
  }

  openNewChat(): void {
    this.chatService.activeSurface = 'classic';
    this.chatService.resetChatState('classic');
  }

  navigateToFullChat(): void {
    const projectId = this.stateConfigService.getProjectId();
    const chatId = this.chatService.session(this.chatService.activeSurface).currentChat?.id;
    this.router.navigate(chatId ? ['projects', projectId, 'chat', chatId] : ['projects', projectId, 'chat']);
  }

  startResizeWidth(event: MouseEvent): void {
    event.preventDefault();
    this.resizeStartX = event.clientX;
    this.panelWasResized = false;
    document.addEventListener('mousemove', this.resizeWidthMoveHandler);
    document.addEventListener('mouseup', this.resizeUpHandler);
  }

  collapseFromHandle(): void {
    if (!this.panelWasResized) {
      this.togglePanel();
    }
    this.panelWasResized = false;
  }

  private unloadHandler = () => {
    this.chatService.stopListeningForNotifications();
  };
}
